import React, { useCallback } from 'react';
import { createStyles } from 'utils/createStyle';
import { ReactComponent as CouponCorner } from 'media/images/coupon-corner.svg';
import { colors } from 'styles/settings';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTicket } from '@fortawesome/pro-light-svg-icons';
import { ReactComponent as CopiedSvg } from 'media/images/copied-tooltip.svg';

export interface DealCouponButtonProps {
    onClick: () => void;
    couponCode: string;
    showCode?: boolean;
}

const dealCouponButtonStyles = createStyles({
    buttonStyle: [
        'relative brand flex flex-row items-center rounded-full rounded-tr-sm text-white text-[18px] font-semibold py-[15px] pl-[30px] md:pl-[44px] pr-[51px] md:pr-[74px]',
        {
            background: 'linear-gradient(270deg, #005E9D 0%, #3EB5E5 98.17%)',
            ':hover': {
                cursor: 'pointer',
            },
        },
    ],
    copiedIndicatorWrapper: [
        'absolute z-10',
        {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -175%)',
        },
    ],
    couponCornerContainer: [
        'absolute right-0 bottom-0 bg-white',
        {
            borderRight: `2px dashed ${colors.brand}`,
            borderBottom: `2px dashed ${colors.brand}`,
        },
    ],
    outlinedCouponButtonStyles: [
        'flex flex-row justify-center text-center items-center border-dashed border border-brand text-dark-blue text-[16px] font-semibold py-[10px] px-[48px] min-w-[347px]',
        {
            ':hover': {
                cursor: 'pointer',
            },
        },
    ],
    ticketIcon: ['text-brand pr-2'],
    visible: [{ opacity: '1' }],
    hidden: [{ opacity: '0', pointerEvents: 'none' }],
});

export const DealCouponButton = ({ onClick, couponCode, showCode }: DealCouponButtonProps) => {
    const [clicked, setClicked] = React.useState(false);
    const [showCopiedIndicator, setShowCopiedIndicator] = React.useState(false);

    const buttonClickHandler = useCallback(() => {
        navigator.clipboard.writeText(couponCode);
        setShowCopiedIndicator(true);
        setClicked(true);

        const timeout = setTimeout(() => {
            onClick?.();
            setShowCopiedIndicator(false);
        }, 1500);

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [onClick]);

    return (
        <div className="relative">
            <div
                className={`${dealCouponButtonStyles.copiedIndicatorWrapper} ${
                    showCopiedIndicator
                        ? dealCouponButtonStyles.visible
                        : dealCouponButtonStyles.hidden
                }`}
            >
                <CopiedSvg />
            </div>

            {clicked || showCode ? (
                <button
                    onClick={buttonClickHandler}
                    className={[
                        dealCouponButtonStyles.outlinedCouponButtonStyles,
                        showCopiedIndicator && 'border-dark-blue',
                    ].join(' ')}
                >
                    <FontAwesomeIcon
                        icon={faTicket}
                        className={[
                            dealCouponButtonStyles.ticketIcon,
                            showCopiedIndicator && 'text-dark-blue',
                        ].join(' ')}
                    />
                    {couponCode}
                </button>
            ) : (
                <button onClick={buttonClickHandler} className={dealCouponButtonStyles.buttonStyle}>
                    Show Coupon &amp; Shop Now
                    <div className={dealCouponButtonStyles.couponCornerContainer}>
                        <CouponCorner />
                    </div>
                </button>
            )}
        </div>
    );
};
